import { useCallback, useEffect, useRef, useState } from "react";
import { usePreviewExplorerCountQuery } from "../store/api/queryBuilderApi";
import { useDebounce } from "./useDebounce";
import {
  cloneQueryParams,
  formatShowPlayersLabel,
  isShowPlayersDisabled,
  toggleRecommendedFilter,
} from "../utils/queryBuilder";

export const useQueryFilterDraft = ({ appliedParams, onConfirm, sport, catalog }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [draftParams, setDraftParams] = useState(() =>
    cloneQueryParams(appliedParams)
  );
  const [debouncedDraft, setDebouncedDraft] = useState(() =>
    cloneQueryParams(appliedParams)
  );
  const lastTotalRef = useRef(null);
  const epochRef = useRef(0);

  const publishDraft = useDebounce((next, epoch) => {
    if (epoch !== epochRef.current) return;
    setDebouncedDraft(next);
  }, 300);

  const openFilters = useCallback(() => {
    epochRef.current += 1;
    const next = cloneQueryParams(appliedParams);
    setDraftParams(next);
    setDebouncedDraft(next);
    lastTotalRef.current = null;
    setIsOpen(true);
  }, [appliedParams]);

  const closeFilters = useCallback(() => {
    epochRef.current += 1;
    setIsOpen(false);
  }, []);

  const updateDraft = useCallback(
    (updater) => {
      setDraftParams((prev) => {
        const next =
          typeof updater === "function" ? updater(prev) : { ...prev, ...updater };
        publishDraft(next, epochRef.current);
        return next;
      });
    },
    [publishDraft]
  );

  const toggleRecommended = useCallback(
    (filterId) => {
      updateDraft((prev) => toggleRecommendedFilter(prev, filterId, catalog));
    },
    [updateDraft, catalog]
  );

  const clearDraft = useCallback(() => {
    const landing = catalog?.landingByGroup?.[catalog?.defaultGroupId];
    const base = landing ? cloneQueryParams(landing) : cloneQueryParams(appliedParams);
    const next = { ...base, statisticalFilters: {}, biographicalFilters: {}, teams: [] };
    setDraftParams(next);
    publishDraft(next, epochRef.current);
  }, [publishDraft, catalog, appliedParams]);

  const confirmFilters = useCallback(() => {
    epochRef.current += 1;
    onConfirm(cloneQueryParams(draftParams));
    setIsOpen(false);
  }, [draftParams, onConfirm]);

  const { data, isFetching, isError } = usePreviewExplorerCountQuery(
    { sport: sport || "mlb", ...debouncedDraft },
    { skip: !isOpen }
  );

  useEffect(() => {
    if (data?.total != null) lastTotalRef.current = data.total;
  }, [data]);

  const hasLoaded = lastTotalRef.current != null || data?.total != null;
  const total = data?.total ?? lastTotalRef.current;
  const showPlayersLabel = formatShowPlayersLabel({
    total,
    isError,
    hasLoaded,
  });
  const showPlayersDisabled = isShowPlayersDisabled({
    total,
    isError,
    hasLoaded,
  });

  return {
    isOpen,
    draftParams,
    openFilters,
    closeFilters,
    confirmFilters,
    clearDraft,
    updateDraft,
    toggleRecommended,
    showPlayersLabel,
    showPlayersDisabled,
    isCountFetching: isFetching,
  };
};
